import React, { useState, useMemo, useCallback } from 'react';
import { useData } from '../../hooks/useData';
import { useApp } from '../../contexts/AppContext';
import { TABLES } from '../../constants/supabaseConstants';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../utils/errorLogging';
import { logger } from '../../utils/logger';
import modalStyles from '../../styles/modals.module.css';
import styles from './BOQNotesTab.module.css';

/**
 * ImportNotesModal — Shows system + architect notes grouped by BB chapter.
 * User selects notes to import as project_boq_notes.
 *
 * Opened via ModalManager with MODAL_TYPES.IMPORT_BOQ_NOTES.
 * Props passed through modalState.data:
 *   - projectId, level0Chapters, existingNotes
 */
const ImportNotesModal = ({ onClose, onSubmitRef, projectId, level0Chapters, existingNotes }) => {
  const { showSuccess, showError } = useApp();
  const { logError } = useErrorLogging();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isImporting, setIsImporting] = useState(false);

  // Fetch system notes
  const { data: systemNotes, isLoading: loadingSystem } = useData(TABLES.REFERENCE_BOQ_NOTES, {
    select: '*',
    enabled: true,
    realtime: false,
  });

  // Fetch architect notes (all authenticated users can read)
  const { data: architectNotes, isLoading: loadingArchitect } = useData(
    TABLES.ARCHITECT_BOQ_NOTES,
    {
      select: '*',
      enabled: true,
      realtime: false,
    },
  );

  // Insert hook for project_boq_notes
  const { insert: insertNote } = useData(TABLES.PROJECT_BOQ_NOTES);

  const isLoading = loadingSystem || loadingArchitect;

  // Merge all template notes with source info, and filter out already-imported ones
  const availableNotes = useMemo(() => {
    const existingSourceIds = new Set((existingNotes || []).map(n => n.source_id).filter(Boolean));
    const notes = [];

    (systemNotes || []).forEach(n => {
      if (!existingSourceIds.has(n.id)) {
        notes.push({ ...n, _sourceType: 'system' });
      }
    });
    (architectNotes || []).forEach(n => {
      if (!existingSourceIds.has(n.id)) {
        notes.push({ ...n, _sourceType: 'architect' });
      }
    });

    return notes;
  }, [systemNotes, architectNotes, existingNotes]);

  // Group available notes by chapter
  const groupedAvailable = useMemo(() => {
    const map = new Map();
    map.set(null, []);

    availableNotes.forEach(n => {
      const key = n.bb_chapter_id || null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(n);
    });

    const groups = [];
    const generalNotes = map.get(null) || [];
    if (generalNotes.length > 0) {
      groups.push({ id: null, name: 'כללי', code: null, notes: generalNotes });
    }

    (level0Chapters || []).forEach(ch => {
      const notes = map.get(ch.id) || [];
      if (notes.length > 0) {
        groups.push({ id: ch.id, name: ch.name_he, code: ch.code, notes });
      }
    });

    return groups;
  }, [availableNotes, level0Chapters]);

  // Toggle individual note selection
  const toggleNote = useCallback(noteId => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  }, []);

  // Toggle entire chapter group
  const toggleGroup = useCallback(group => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = group.notes.every(n => next.has(n.id));
      group.notes.forEach(n => {
        if (allSelected) next.delete(n.id);
        else next.add(n.id);
      });
      return next;
    });
  }, []);

  // Import selected notes
  const handleImport = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setIsImporting(true);
    try {
      // Get max display_order of existing notes per chapter to continue ordering
      const maxOrders = new Map();
      (existingNotes || []).forEach(n => {
        const key = n.bb_chapter_id || '__general__';
        maxOrders.set(key, Math.max(maxOrders.get(key) ?? 0, n.display_order ?? 0));
      });

      const notesToInsert = availableNotes
        .filter(n => selectedIds.has(n.id))
        .map(n => {
          const chapterKey = n.bb_chapter_id || '__general__';
          const order = (maxOrders.get(chapterKey) ?? 0) + 1;
          maxOrders.set(chapterKey, order);
          return {
            project_id: projectId,
            text: n.text,
            bb_chapter_id: n.bb_chapter_id || null,
            display_order: order,
            enabled: true,
            source_id: n.id,
            source_type: n._sourceType,
          };
        });

      // Insert all notes
      await Promise.all(notesToInsert.map(note => insertNote(note)));
      showSuccess(`יובאו ${notesToInsert.length} הערות בהצלחה`);
      onClose();
    } catch (err) {
      logger.error('Failed to import notes', err);
      showError('שגיאה בייבוא הערות');
      logError(err, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_MUTATION,
        message: 'Failed to import BOQ notes',
      });
    } finally {
      setIsImporting(false);
    }
  }, [
    selectedIds,
    availableNotes,
    existingNotes,
    projectId,
    insertNote,
    onClose,
    showSuccess,
    showError,
    logError,
  ]);

  // Wire up Enter key submit via ModalManager
  if (onSubmitRef) onSubmitRef(handleImport);

  return (
    <div className={modalStyles.modalLarge} dir="rtl" onClick={e => e.stopPropagation()}>
      {/* Header */}
      <div className={modalStyles.header}>
        <h2 className={modalStyles.headerTitle}>ייבא הערות</h2>
        <p className={modalStyles.headerSubtitle}>בחר הערות מתבניות מערכת ואדריכל לייבוא לפרויקט</p>
      </div>

      {/* Content */}
      <div className={modalStyles.content}>
        {isLoading ? (
          <div className={modalStyles.loading}>טוען תבניות...</div>
        ) : availableNotes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
            אין הערות זמינות לייבוא (כל ההערות כבר יובאו)
          </div>
        ) : (
          groupedAvailable.map(group => (
            <div key={group.id || '__general__'} className={modalStyles.section}>
              {/* Chapter group header with select all */}
              <div
                className={modalStyles.checkboxField}
                style={{
                  padding: '10px 0',
                  borderBottom: '1px solid #e2e8f0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
                onClick={() => toggleGroup(group)}
              >
                <input
                  type="checkbox"
                  checked={group.notes.length > 0 && group.notes.every(n => selectedIds.has(n.id))}
                  readOnly
                />
                <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', flex: 1 }}>
                  {group.name}
                  {group.code && (
                    <span
                      style={{
                        color: '#64748b',
                        fontSize: 13,
                        fontWeight: 400,
                        marginInlineStart: 8,
                      }}
                    >
                      ({group.code})
                    </span>
                  )}
                </span>
                <span style={{ color: '#94a3b8', fontSize: 12 }}>{group.notes.length} הערות</span>
              </div>

              {/* Individual notes */}
              {group.notes.map(note => (
                <div
                  key={note.id}
                  className={modalStyles.checkboxField}
                  style={{
                    padding: '10px 20px',
                    cursor: 'pointer',
                    backgroundColor: selectedIds.has(note.id) ? '#eff6ff' : 'transparent',
                    borderRadius: 6,
                    transition: 'background-color 0.15s',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                  }}
                  onClick={() => toggleNote(note.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(note.id)}
                    readOnly
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ flex: 1, fontSize: 14, color: '#334155', lineHeight: 1.5 }}>
                    {note.text}
                  </span>
                  <span
                    className={
                      note._sourceType === 'system'
                        ? styles.sourceBadgeSystem
                        : styles.sourceBadgeArchitect
                    }
                  >
                    {note._sourceType === 'system' ? 'מערכת' : 'אדריכל'}
                  </span>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className={modalStyles.footerEnd}>
        <button className={modalStyles.cancelButton} onClick={onClose}>
          ביטול
        </button>
        <button
          className={modalStyles.primaryButton}
          onClick={handleImport}
          disabled={selectedIds.size === 0 || isImporting}
        >
          {isImporting ? 'מייבא...' : `ייבא ${selectedIds.size} הערות`}
        </button>
      </div>
    </div>
  );
};

export default ImportNotesModal;
